import { defineStore } from 'pinia';
import useViewsApi from '~/composables/api/useViewsApi';
import { toField } from '~/utils/form-field';
import { MAX_VIEW_NAME_LENGTH } from '~/composables/common/data-grid/useViewNameValidation';
import { TRANSACTIONS_ALL_TABLE_IDENTIFIER } from '~/types/views';
import { isReservedViewName } from '~/utils/view-display-name';
import type { CreateViewBody, FieldValue, SavedView, ViewConfig } from '~/types';

type GridHandlers = {
  apply: (view: SavedView) => void;
  capture: () => SavedView['config'];
  resetBaseline: () => void;
};
let gridHandlers: GridHandlers | null = null;

export const useViewsStore = defineStore('views', {
  state: () => ({
    views: [] as SavedView[],
    activeViewId: null as string | null,
    hasUnsavedChanges: false,
    draftConfig: null as ViewConfig | null,
    draftViewId: null as string | null,
    loaded: false,
    saveModalOpen: false,
    saveViewName: toField() as FieldValue,
    saveViewSetAsDefault: false,
    serverDuplicateError: false,
    pendingCreateConfig: null as ViewConfig | null,
    manageModalOpen: false
  }),
  getters: {
    activeView(state): SavedView | undefined {
      return state.views.find((view) => view.id === state.activeViewId);
    },
    defaultView(state): SavedView | undefined {
      return state.views.find((view) => view.isDefault);
    },
    hasDraftForActive(state): boolean {
      return (
        state.hasUnsavedChanges &&
        state.draftConfig !== null &&
        state.draftViewId === state.activeViewId
      );
    }
  },
  actions: {
    registerGridHandlers(handlers: GridHandlers) {
      gridHandlers = handlers;
    },
    unregisterGridHandlers(handlers: GridHandlers) {
      if (gridHandlers === handlers) gridHandlers = null;
    },
    async loadViews() {
      try {
        const { getViews } = useViewsApi();
        this.views = await getViews();
      } catch {
        this.views = [];
      }
      this.loaded = true;
    },
    async createView(body: CreateViewBody) {
      const { createView, getViews } = useViewsApi();
      const created = await createView(body);
      try {
        this.views = await getViews();
      } catch {
        this.views.push(created);
      }
      this.activeViewId = created.id;
      this.hasUnsavedChanges = false;
      this.clearDraft();
      return created;
    },
    setActiveView(viewId: string | null) {
      this.activeViewId = viewId;
      this.hasUnsavedChanges = false;
    },
    setHasUnsavedChanges(value: boolean) {
      this.hasUnsavedChanges = value;
    },
    setDraft(config: ViewConfig) {
      this.draftConfig = config;
      this.draftViewId = this.activeViewId;
    },
    clearDraft() {
      this.draftConfig = null;
      this.draftViewId = null;
    },
    isNameTaken(name: string) {
      const normalized = name.trim().toLowerCase();
      const matchesExistingView = this.views.some(
        (view) => view.name.trim().toLowerCase() === normalized
      );
      return matchesExistingView || isReservedViewName(name);
    },
    resetSaveViewForm() {
      this.saveViewName = toField();
      this.saveViewSetAsDefault = false;
    },
    openSaveModal() {
      this.resetSaveViewForm();
      this.saveModalOpen = true;
    },
    closeSaveModal() {
      this.saveModalOpen = false;
      this.pendingCreateConfig = null;
    },
    requestSelectView(viewId: string) {
      const view = this.views.find((candidate) => candidate.id === viewId);
      if (view) gridHandlers?.apply(view);
    },
    async requestSaveView(name: string) {
      if (!gridHandlers) return;
      const config = this.pendingCreateConfig ?? gridHandlers.capture();
      await this.createView({ name, config });
      this.pendingCreateConfig = null;
      gridHandlers.resetBaseline();
      this.closeSaveModal();
    },
    async saveEdits() {
      if (!gridHandlers || !this.activeViewId || !this.activeView) return;
      const { updateView, getViews } = useViewsApi();
      const capturedConfig = gridHandlers.capture();
      const body = {
        name: this.activeView.name,
        viewContent: { name: this.activeView.name, ...capturedConfig }
      };
      await updateView(this.activeViewId, body);
      try {
        this.views = await getViews();
      } catch {
        const idx = this.views.findIndex((v) => v.id === this.activeViewId);
        if (idx !== -1) {
          this.views[idx] = { ...this.views[idx], config: capturedConfig };
        }
      }
      gridHandlers.resetBaseline();
    },
    createViewFromDefault() {
      if (!this.defaultView) return;
      this.pendingCreateConfig = { ...this.defaultView.config };
      this.openSaveModal();
    },
    clearServerDuplicateError() {
      this.serverDuplicateError = false;
    },
    openManageModal() {
      this.manageModalOpen = true;
    },
    closeManageModal() {
      this.manageModalOpen = false;
    },
    async manageRenameView(id: string, name: string) {
      const { renameView, getViews } = useViewsApi();
      await renameView(id, { name });
      this.views = await getViews();
    },
    buildDuplicateName(sourceName: string) {
      const buildCandidate = (suffix: string) => {
        const base = sourceName.slice(0, MAX_VIEW_NAME_LENGTH - suffix.length);
        return `${base}${suffix}`;
      };

      let candidate = buildCandidate(' (copy)');
      let attempt = 2;
      while (this.isNameTaken(candidate)) {
        candidate = buildCandidate(` (copy ${attempt})`);
        attempt += 1;
      }
      return candidate;
    },
    async manageDuplicateView(id: string) {
      const sourceView = this.views.find((view) => view.id === id);
      const name = this.buildDuplicateName(sourceView?.name ?? 'View');
      const { duplicateView, getViews } = useViewsApi();
      const created = await duplicateView(id, { name });
      this.views = await getViews();
      return created;
    },
    async manageSetFavoriteView(id: string) {
      const { setDefaultView, getViews } = useViewsApi();
      await setDefaultView(id);
      this.views = await getViews();
    },
    async manageReorderViews(
      orderedIds: string[],
      tableIdentifier: string = TRANSACTIONS_ALL_TABLE_IDENTIFIER
    ) {
      const { reorderViews, getViews } = useViewsApi();
      await reorderViews(orderedIds, tableIdentifier);
      this.views = await getViews();
    },
    async manageDeleteView(id: string) {
      const deletedView = this.views.find((view) => view.id === id);
      const { deleteView, getViews } = useViewsApi();
      await deleteView(id);
      this.views = await getViews();
      if (!deletedView || this.activeViewId !== id) return;

      if (deletedView.isDefault) {
        const firstRemaining = this.views[0];
        if (firstRemaining) this.requestSelectView(firstRemaining.id);
        return;
      }
      const lemonwayView = this.views.find((view) => view.scope === 'global');
      if (lemonwayView) this.requestSelectView(lemonwayView.id);
    }
  }
});
